/**
 * LocationCard - Location selection component for listing forms
 *
 * Uses LocationDropdown with GeoStore for consistent location selection.
 * Layout: Prefecture | City, Ward | Address, then Validate button.
 * After validation: NearestStationSelector appears.
 */

import { useState, useCallback, useEffect } from "react";
import { observer } from "mobx-react-lite";

import { Button, Input, Select } from "@/shared/ui/components";
import { Box } from "@/shared/ui/base";
import {
  NearestStationSelector,
  type SelectedStation,
} from "../NearestStationSelector/NearestStationSelector";
import { useStore } from "@/stores/useStore";
import { useTranslation } from "@/shared/hooks";
import type {
  SupportedLanguage,
  Prefecture,
  City,
  Location as GeoLocation,
} from "../../types";

export interface LocationFormData {
  prefecture: string;
  city: string;
  ward?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
}

export interface LocationCardProps {
  formData: LocationFormData;
  onFormDataChange: (field: keyof LocationFormData, value: unknown) => void;
  selectedStations: SelectedStation[];
  onStationsChange: (stations: SelectedStation[]) => void;
}

export const LocationCard = observer((props: LocationCardProps) => {
  const { formData, onFormDataChange, selectedStations, onStationsChange } =
    props;
  const { uiStore, geoStore } = useStore();
  const t = useTranslation();
  const lang = uiStore.locale as SupportedLanguage;

  // Local state for managing API dependencies
  const [selectedPrefectureCode, setSelectedPrefectureCode] = useState<
    number | undefined
  >(undefined);
  const [selectedCitySlug, setSelectedCitySlug] = useState<string | undefined>(
    undefined,
  );

  // Local state for loaded data (to ensure reactivity)
  const [prefectures, setPrefectures] = useState<Prefecture[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [locations, setLocations] = useState<GeoLocation[]>([]);

  // Local state for validation
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | undefined>(
    undefined,
  );
  const [isAddressValidated, setIsAddressValidated] = useState(false);
  const [validatedCoords, setValidatedCoords] = useState<{
    lat: number;
    lon: number;
  } | null>(null);

  // Load prefectures on mount
  useEffect(() => {
    const loadData = async () => {
      const data = await geoStore.loadPrefectures(lang);
      setPrefectures(data);
    };
    loadData();
  }, [geoStore, lang]);

  // Load cities when prefecture changes
  useEffect(() => {
    if (selectedPrefectureCode) {
      const loadData = async () => {
        const data = await geoStore.loadCities(selectedPrefectureCode, lang);
        setCities(data);
      };
      loadData();
    } else {
      setCities([]);
    }
  }, [geoStore, selectedPrefectureCode, lang]);

  // Load locations/wards when city changes
  useEffect(() => {
    if (selectedCitySlug) {
      const loadData = async () => {
        const data = await geoStore.loadLocations({
          citySlug: selectedCitySlug,
          lang,
        });
        setLocations(data);
      };
      loadData();
    } else {
      setLocations([]);
    }
  }, [geoStore, selectedCitySlug, lang]);

  // Convert to Select options
  const prefectureOptions = prefectures.map((p) => ({
    value: p.code,
    label:
      lang === "ja"
        ? p.name.ja
        : lang === "ru"
          ? p.name.ru || p.name.en
          : p.name.en,
  }));

  const cityOptions = cities.map((c) => ({
    value: c.city_slug,
    label:
      lang === "ja"
        ? c.name.ja
        : lang === "ru"
          ? c.name.ru || c.name.en
          : c.name.en,
  }));

  const wardOptions = locations.map((l) => ({
    value: l.name.ja,
    label:
      lang === "ja"
        ? l.name.ja
        : lang === "ru"
          ? l.name.ru || l.name.en
          : l.name.en,
  }));

  // Reverse lookup prefecture code when editing
  useEffect(() => {
    if (
      formData.prefecture &&
      prefectures.length > 0 &&
      !selectedPrefectureCode
    ) {
      const matchedPref = prefectures.find(
        (p) => p.name.ja === formData.prefecture,
      );
      if (matchedPref) {
        setSelectedPrefectureCode(matchedPref.code);
      }
    }
  }, [formData.prefecture, prefectures, selectedPrefectureCode]);

  // Reverse lookup city slug when editing
  useEffect(() => {
    if (
      formData.city &&
      cities.length > 0 &&
      !selectedCitySlug &&
      selectedPrefectureCode
    ) {
      const matchedCity = cities.find((c) => c.name.ja === formData.city);
      if (matchedCity) {
        setSelectedCitySlug(matchedCity.city_slug);
      }
    }
  }, [formData.city, cities, selectedCitySlug, selectedPrefectureCode]);

  // Auto-validate when editing if coordinates already exist
  useEffect(() => {
    if (
      formData.address &&
      !isAddressValidated &&
      formData.latitude &&
      formData.longitude
    ) {
      setIsAddressValidated(true);
      setValidatedCoords({ lat: formData.latitude, lon: formData.longitude });
    }
  }, [
    formData.address,
    formData.latitude,
    formData.longitude,
    isAddressValidated,
  ]);

  // Handle prefecture change
  const handlePrefectureChange = useCallback(
    (value: string | number) => {
      const code = Number(value);
      const selected = prefectures.find((p) => p.code === code);
      if (selected) {
        onFormDataChange("prefecture", selected.name.ja);
        setSelectedPrefectureCode(code);
        // Clear dependent fields
        onFormDataChange("city", "");
        onFormDataChange("ward", "");
        onFormDataChange("address", "");
        setSelectedCitySlug(undefined);
        // Invalidate address and clear stations
        setIsAddressValidated(false);
        setValidatedCoords(null);
        onFormDataChange("latitude", undefined);
        onFormDataChange("longitude", undefined);
        onStationsChange([]);
      }
    },
    [prefectures, onFormDataChange, onStationsChange],
  );

  // Handle city change
  const handleCityChange = useCallback(
    (value: string | number) => {
      const slug = String(value);
      const selected = cities.find((c) => c.city_slug === slug);
      if (selected) {
        onFormDataChange("city", selected.name.ja);
        setSelectedCitySlug(slug);
        // Clear dependent fields
        onFormDataChange("ward", "");
        onFormDataChange("address", "");
        // Invalidate address and clear stations
        setIsAddressValidated(false);
        setValidatedCoords(null);
        onFormDataChange("latitude", undefined);
        onFormDataChange("longitude", undefined);
        onStationsChange([]);
      }
    },
    [cities, onFormDataChange, onStationsChange],
  );

  // Handle ward change
  const handleWardChange = useCallback(
    (value: string | number) => {
      onFormDataChange("ward", String(value));
      // Ward change should also reset address
      onFormDataChange("address", "");
      setIsAddressValidated(false);
      setValidatedCoords(null);
      onFormDataChange("latitude", undefined);
      onFormDataChange("longitude", undefined);
      onStationsChange([]);
    },
    [onFormDataChange, onStationsChange],
  );

  // Handle address change
  const handleAddressChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newAddress = e.target.value;
      onFormDataChange("address", newAddress);
      setIsAddressValidated(false);
      setValidatedCoords(null);
      onFormDataChange("latitude", undefined);
      onFormDataChange("longitude", undefined);
      // Clear stations when address changes
      if (selectedStations.length > 0) {
        onStationsChange([]);
      }
    },
    [onFormDataChange, selectedStations.length, onStationsChange],
  );

  // Validate address using Google API
  const handleValidateAddress = useCallback(async () => {
    if (!formData.address) {
      setValidationError(
        t("createListingInvalidAddress") || "Please enter an address",
      );
      return;
    }

    setIsValidating(true);
    setValidationError(undefined);

    try {
      const result = await geoStore.validateAddress({
        address: formData.address,
        locality: formData.city,
      });

      if (result.is_valid && result.lat && result.lon) {
        setIsAddressValidated(true);
        setValidatedCoords({ lat: result.lat, lon: result.lon });
        onFormDataChange("latitude", result.lat);
        onFormDataChange("longitude", result.lon);
      } else {
        setIsAddressValidated(false);
        setValidationError(
          t("createListingAddressValidationError") ||
            "Could not validate address",
        );
        onFormDataChange("latitude", undefined);
        onFormDataChange("longitude", undefined);
      }
    } catch {
      setIsAddressValidated(false);
      setValidationError(
        t("createListingAddressValidationError") || "Address validation failed",
      );
      onFormDataChange("latitude", undefined);
      onFormDataChange("longitude", undefined);
    } finally {
      setIsValidating(false);
    }
  }, [formData.address, formData.city, geoStore, onFormDataChange, t]);

  return (
    <>
      {/* 2x2 Grid: Prefecture | City, Ward | Address */}
      <Box className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          label={t("searchPrefecture") || "Prefecture"}
          value={selectedPrefectureCode || ""}
          onChange={handlePrefectureChange}
          options={prefectureOptions}
          placeholder={t("searchSelectPrefecture") || "Select prefecture"}
          required
        />
        <Select
          label={t("searchCity") || "City"}
          value={selectedCitySlug || ""}
          onChange={handleCityChange}
          options={cityOptions}
          placeholder={t("searchSelectCity") || "Select city"}
          disabled={!selectedPrefectureCode}
          required
        />
        <Select
          label={t("createListingWard") || "Ward/Location"}
          value={formData.ward || ""}
          onChange={handleWardChange}
          options={wardOptions}
          placeholder={t("searchSelectArea") || "Select area"}
          disabled={!selectedCitySlug}
        />
        <Input
          label={t("createListingAddress") || "Address"}
          value={formData.address || ""}
          onChange={handleAddressChange}
          error={validationError}
          helperText={
            isValidating
              ? t("createListingValidatingAddress") || "Validating..."
              : t("createListingAddressHint") || "Enter street address"
          }
          disabled={!selectedCitySlug}
        />
      </Box>

      {/* Validate Address Button - Full Width, shown only when NOT validated */}
      {!isAddressValidated && (
        <Button
          variant="primary"
          onClick={handleValidateAddress}
          disabled={isValidating || !formData.address}
          loading={isValidating}
          className="w-full h-14 mt-4"
        >
          {isValidating
            ? t("createListingValidating") || "Validating..."
            : t("createListingValidate") || "Validate Address"}
        </Button>
      )}

      {/* Station selector shows when address is validated */}
      {isAddressValidated && (
        <Box className="mt-4">
          <NearestStationSelector
            address={formData.address}
            citySlug={selectedCitySlug}
            selectedStations={selectedStations}
            onSelectionChange={onStationsChange}
            maxStations={5}
            radiusM={1500}
            label={t("createListingStation") || "Nearest Stations"}
            lang={lang}
            isAddressValidated={isAddressValidated}
            validatedLat={validatedCoords?.lat}
            validatedLon={validatedCoords?.lon}
          />
        </Box>
      )}
    </>
  );
});
